/**
 * Insurance, drawn instead of described (2026-09-02).
 *
 * User: "Insurance also want to be visually made and described as we did with
 * other spots."
 *
 * The panel was one sentence and one button. That is a poor showing for the
 * subtlest mechanic in the game — `design/gdd/insurance.md` is explicit that
 * insurance is "a quiet system, not a flashy one: most of its value is felt
 * exactly once, at the moment of death". A player who has never died with an
 * insured item has no way to find out what 500 Wax buys them, and the one
 * genuinely confusing thing about it — that you buy *capacity* here but
 * choose the *items* mid-run — was nowhere on the screen.
 *
 * ## Capacity is drawn as slots, because that is what it is
 *
 * Two frames: the ones you own, and the one you do not. An owned slot carries
 * the shield; a locked one carries a padlock and its price. That is the same
 * move the map picker made — show the thing being bought, including the part
 * you already have, so the purchase is a comparison rather than a leap.
 *
 * ## The facts are derived, not written
 *
 * "Max 2 slots" reads off the server's own `maxSlots` (added to
 * `GET /api/insurance` for exactly this), so a retune of
 * `MAX_INSURANCE_SLOTS` cannot leave this panel claiming a number the backend
 * no longer enforces — the same rule `stratum-info.ts` follows for the
 * Contracts cards.
 *
 * Pure string builders, so the interesting states — first slot only, at the
 * cap, cannot afford — are testable without a hub or a server.
 */

import { amountMarkup } from './currency';
import { esc } from './item-icon';
import { t, tp, N_ } from '../i18n/i18n';

const SHIELD_SRC = '/assets/ui/insurance/shield.webp';

/**
 * The one paragraph.
 *
 * Written to answer the two questions the old sentence left open: what does a
 * slot actually do, and where do I use it. The GDD's own framing — "the
 * difference between walking away with something and walking away with
 * nothing" — is the line worth putting in front of a player.
 */
// Marked, not translated: the hub draws it with t(INSURANCE_DESCRIPTION).
export const INSURANCE_DESCRIPTION = N_('Death costs you everything you were carrying — except what you insured. Cover is bought one run at a time; the slots it fills are bought once and kept forever. Which items go in them is decided down in the dark, on the items you are actually holding. Extract alive and none of it matters. Die, and it is the difference between walking out with something and walking out with nothing.');

/** A padlock, drawn rather than fetched — same reasoning as the currency glyphs. */
function lockGlyph(): string {
  return '<svg viewBox="0 0 24 24" width="44%" height="44%" aria-hidden="true" focusable="false"'
    + ' class="inline-block"><path d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5" fill="none" stroke="#241309"'
    + ' stroke-width="2.2" stroke-linecap="round"/><rect x="4.6" y="10.2" width="14.8" height="10.4" rx="2.4"'
    + ' fill="#a49c94" stroke="#241309" stroke-width="2"/><circle cx="12" cy="15.4" r="1.7" fill="#241309"/></svg>';
}

export interface InsuranceView {
  /** How many items one covered run protects. Permanent. */
  insuranceSlots: number;
  maxSlots: number;
  nextExpandCostWax: number | null;
  /** How many covered runs are left. Spent by a run that used cover. */
  charges: number;
  maxCharges: number;
  chargeCostWax: number;
}

/**
 * One slot frame.
 *
 * The owned and locked frames are the same size and shape on purpose: the
 * locked one should read as "a slot you don't have yet", not as a different
 * kind of object.
 */
function slotMarkup(index: number, owned: boolean, costWax: number | null): string {
  const frame = owned
    ? 'border-gold/70 bg-[radial-gradient(circle_at_50%_35%,rgba(224,166,64,0.22),transparent_70%)]'
    : 'border-border bg-black/20';
  const body = owned
    ? `<img src="${SHIELD_SRC}" alt="" aria-hidden="true" class="absolute inset-0 w-full h-full object-contain p-3" />`
    : `<span class="absolute inset-0 grid place-items-center opacity-75">${lockGlyph()}</span>`;
  const status = owned
    ? `<span class="text-[11px] text-gold">${esc(t('Protected'))}</span>`
    : costWax !== null
      ? `<span class="text-[11px]">${amountMarkup('wax', costWax, { hideLabel: true })}</span>`
      : `<span class="text-[11px] text-text-dim">${esc(t('Locked'))}</span>`;
  return `
    <div class="flex flex-col items-center gap-1.5">
      <div class="relative w-full aspect-square rounded-lg border-2 ${frame} overflow-hidden">${body}</div>
      <span class="text-[11px] text-text-dim">${esc(t('Slot {n}', { n: index }))}</span>
      ${status}
    </div>
  `;
}

/** Every slot the game has, owned or not — so the cap is visible before you hit it. */
export function insuranceSlotsMarkup(view: InsuranceView): string {
  const total = Math.max(1, view.maxSlots);
  const slots: string[] = [];
  for (let i = 1; i <= total; i++) {
    slots.push(slotMarkup(i, i <= view.insuranceSlots, i === view.insuranceSlots + 1 ? view.nextExpandCostWax : null));
  }
  return `<div class="grid grid-cols-2 gap-3 max-w-[19rem]">${slots.join('')}</div>`;
}

/**
 * The rules, as chips.
 *
 * Three facts, each one a thing a player would otherwise have to die to
 * learn. `maxSlots` comes from the server so this cannot drift from what is
 * enforced.
 */
export function insuranceFactsMarkup(view: InsuranceView): string {
  const chips = [
    t('Only insured items survive death'),
    t('Chosen in the run, not here'),
    // The rule this system turns on, stated where it cannot be missed: cover
    // is per-run now, and a run that used it spends it.
    t('Cover lasts one run'),
    tp(view.maxSlots, 'Max {n} slot', 'Max {n} slots'),
  ];
  return `<div class="flex flex-wrap gap-1.5">${chips
    .map((c) => `<span class="chip">${esc(c)}</span>`)
    .join('')}</div>`;
}

/**
 * The tile's one-line summary.
 *
 * Leads with cover rather than capacity, because cover is the half that can
 * be zero. A player glancing at the hub needs to know whether their next
 * delve is protected at all before they need to know how widely.
 */
export function insuranceTileSub(view: InsuranceView): string {
  if (view.charges <= 0) return t('No cover');
  return tp(view.charges, '{n} run covered', '{n} runs covered');
}

/**
 * The cover block: how many runs are protected, and how to buy more.
 *
 * Separate from the slot grid above it because the two sell different things
 * and were being confused for one. Slots widen the cover; charges are the
 * cover. The line says which is which in the fewest words that stay true.
 */
export function insuranceCoverMarkup(view: InsuranceView): string {
  const none = view.charges <= 0;
  const full = view.charges >= view.maxCharges;
  return `
    <div class="frame-inset p-3 flex flex-wrap items-center gap-x-3 gap-y-2">
      <div class="min-w-0 flex-1">
        <p class="font-heading ${none ? 'text-error' : 'text-gold'}">
          ${esc(none ? t('No cover') : tp(view.charges, '{n} run covered', '{n} runs covered'))}
        </p>
        <p class="text-[12px] text-text-dim">
          ${none
            ? esc(t('Die without cover and everything you are carrying is gone.'))
            : esc(t('One run each. A delve where you insure nothing costs no cover.'))}
        </p>
      </div>
      ${full
        ? `<span class="text-[12px] text-text-dim shrink-0">${esc(t('Stack full'))}</span>`
        : `<button id="hub-insurance-buy-cover" type="button" class="btn btn-gold px-4 shrink-0">
             <span class="inline-flex items-center gap-1">${esc(t('Buy cover'))}${
               amountMarkup('wax', view.chargeCostWax, { hideLabel: true, tone: 'text-bg' })}</span>
           </button>`}
    </div>`;
}

/** The expand control's label — a price while there is one to pay, a state once there is not. */
export function insuranceExpandLabel(view: InsuranceView): { html: string; disabled: boolean } {
  if (view.nextExpandCostWax === null) {
    return { html: `<span class="text-text-dim">${esc(tp(view.maxSlots, '{n} slot owned, the maximum', 'All {n} slots owned'))}</span>`, disabled: true };
  }
  return {
    html: `<span class="inline-flex items-center gap-1">${esc(t('Buy slot {n}', { n: view.insuranceSlots + 1 }))}`
      + `${amountMarkup('wax', view.nextExpandCostWax, { hideLabel: true })}</span>`,
    disabled: false,
  };
}
